import type { FC } from 'react';
import React, { Fragment, useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl-next';
import { differenceInDays } from 'date-fns';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { token } from '@atlaskit/tokens';
import Button, { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Avatar from '@atlaskit/avatar';

import { fg } from '@confluence/feature-gating';
import { useFeatureDiscovery } from '@confluence/feature-discovery';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import {
	useGetOnboardingState,
	useSetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import {
	NTH_USER_AUTO_FOLLOW_SPACES,
	NTH_USER_AUTO_FOLLOW_TEAMMATES,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

import { onboardingFeedHeaderCard } from '../feedConstants';

import FollowingOnboardingImageDark from './images/FeedOnboardingMessageDark.svg';
import FollowingOnboardingImageLight from './images/FeedOnboardingMessageLight.svg';
import FollowingOnboardingImageDarkV2 from './images/FeedOnboardingMessageV2Dark.svg';
import FollowingOnboardingImageLightV2 from './images/FeedOnboardingMessageV2Light.svg';

export const i18n = defineMessages({
	headerTitle: {
		id: 'feed.header.title',
		defaultMessage: 'We’re keeping you in the loop',
		description: 'Title header from feed onboarding message',
	},
	headerDescription: {
		id: 'feed.header.description',
		defaultMessage:
			'Stay in-the-know by following people and spaces. Their activity will show up in your feed, but you won’t receive email notifications about it. Add to, or edit, your feed anytime.',
		description:
			"Description for feed onboarding message. 'they' being the people followed from the title message",
	},
	editFeed: {
		id: 'feed.header.editFeed',
		defaultMessage: 'Edit feed',
		description: 'Button from feed onboarding message to bring up user settings modal',
	},
	closeMessageLabel: {
		id: 'feed.header.closeLabel',
		defaultMessage: 'Close message box',
		description: 'label for the close icon to dismiss the message box',
	},
	autofollowBannerDismiss: {
		id: 'feed.header.autofollow.banner.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Dismiss button for the autofollow experiments banners',
	},
	headerDescriptionV2: {
		id: 'feed.header.description.v2',
		defaultMessage:
			'We’ve automatically followed top recommended spaces and people for you, so you never miss important updates. You can add or remove spaces and people from your feed at any time to tailor what you see.',
		description: 'Description for feed onboarding message to explain how feed customization works',
	},
	customizeFeed: {
		id: 'feed.header.customizeFeed',
		defaultMessage: 'Customize feed',
		description: 'Button from feed onboarding message to bring up user settings modal',
	},
	autofollowTeammatesTitle: {
		id: 'feed.header.autofollow-teammates.title',
		defaultMessage:
			"{count, plural, one {To get you started, we've added {name1} to your feed} =2 {To get you started, we've added {name1} and {name2} to your feed} other {To get you started, we've added {name1}, {name2}, and others to your feed}}",
		description:
			'Title when teammates have been automatically followed in feed, {name1, name2} will be replaced with the actual teammate names',
	},
	autofollowTeammatesDescription: {
		id: 'feed.header.autofollow-teammates.description',
		defaultMessage: 'Use <b>Edit feed</b> to choose the people and spaces you want to follow',
		description: 'Description explaining how to customize feed after autofollow teammates',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BodyContainer = styled.div({
	boxSizing: 'border-box',
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	background: token('elevation.surface'),
	display: 'flex',
	marginBottom: token('space.300'),
	padding: `${token('space.300')} ${token('space.300')} 0`,
});

const autofollowTeammatesStyles = cssMap({
	autofollowTeammatesContainer: {
		boxSizing: 'border-box',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
		display: 'flex',
		marginBottom: token('space.300'),
		paddingTop: token('space.300'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.200'),
	},
	autofollowAvatarContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		marginRight: token('space.100'),
		flexShrink: 'none',
		position: 'relative',
	},
	avatarOverlap: {
		marginLeft: token('space.negative.050'),
	},
	autofollowTeammatesMessage: {
		flexGrow: 1,
		marginRight: token('space.250'),
		marginBottom: token('space.0'),
	},
	autofollowTeammatesDescription: {
		marginBottom: token('space.050'),
	},
	autofollowCloseButtonContainer: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageContainer = styled.div({
	flexGrow: 1,
	marginRight: token('space.250'),
	marginBottom: token('space.300'),
});

const descriptionTextStyles = xcss({
	marginBottom: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OnBoardingImage = styled(Image)({
	maxWidth: '200px',
	maxHeight: '160px',
	flexShrink: 0,
	alignSelf: 'flex-end',
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CloseButtonContainer = styled.div({
	flexShrink: 0,
});

type FollowedUser = {
	displayName?: string;
	profilePicture?: {
		path?: string;
	};
	accountId?: string;
	__typename?: string;
};

type FeedOnboardingMessageProps = {
	openActivityFilterModal: () => void;
	servingRecommendations: boolean;
	followedUsers?: FollowedUser[];
} & WrappedComponentProps;

const defaultContentConfig = {
	lightImage: FollowingOnboardingImageLight,
	darkImage: FollowingOnboardingImageDark,
	description: (
		<Text size="large" weight="regular">
			<FormattedMessage {...i18n.headerDescription} />
		</Text>
	),
	button: <FormattedMessage {...i18n.editFeed} />,
	title: (
		<Heading size="small" as="h3">
			<Text size="large" weight="regular">
				<FormattedMessage {...i18n.headerTitle} />
			</Text>
		</Heading>
	),
};

const autoFollowContentConfig = {
	lightImage: FollowingOnboardingImageLightV2,
	darkImage: FollowingOnboardingImageDarkV2,
	description: (
		<Text size="large" weight="regular" color="color.text.subtle">
			<FormattedMessage {...i18n.headerDescriptionV2} />
		</Text>
	),
	button: (
		<Text color="color.text.subtle">
			<FormattedMessage {...i18n.customizeFeed} />
		</Text>
	),
	title: (
		<Heading size="small">
			<FormattedMessage {...i18n.headerTitle} />
		</Heading>
	),
};

export const getAutofollowTeammatesContentConfig = (
	teammateCount: number,
	name1: string,
	name2: string,
) => ({
	lightImage: null,
	darkImage: null,
	description: (
		<Text color="color.text.subtle">
			<FormattedMessage
				{...i18n.autofollowTeammatesDescription}
				values={{ b: (chunks) => <b>{chunks}</b> }}
			/>
		</Text>
	),
	button: null,
	title: (
		<Heading size="small">
			<FormattedMessage
				{...i18n.autofollowTeammatesTitle}
				values={{
					count: teammateCount,
					name1,
					name2,
				}}
			/>
		</Heading>
	),
});

const FeedOnboardingMessageComponent: FC<FeedOnboardingMessageProps> = ({
	intl,
	openActivityFilterModal,
	servingRecommendations,
	followedUsers = [],
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [showFeature, stop, featureDiscoveryError] = useFeatureDiscovery(onboardingFeedHeaderCard);

	const { setOnboardingState } = useSetOnboardingState();

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[
			NTH_USER_AUTO_FOLLOW_SPACES.HAS_AUTO_FOLLOWED_SPACES,
			NTH_USER_AUTO_FOLLOW_TEAMMATES.HAS_AUTO_FOLLOWED_TEAMMATES,
			NTH_USER_AUTO_FOLLOW_TEAMMATES.AUTO_FOLLOW_BANNER_DISMISSED,
			NTH_USER_AUTO_FOLLOW_TEAMMATES.AUTO_FOLLOW_BANNER_SHOWN_AT,
		],
		!fg('cc-engagement-auto-follow-recommended-spaces-gate') &&
			!fg('autofollow-teammates-preconditions'), // skip if both fgs are not enabled
	);

	const {
		hasAutoFollowedSpaces,
		hasAutoFollowedTeammates,
		autoFollowBannerDismissed,
		autoFollowBannerShownAt,
	} = deserializeState(onboardingStateData);

	const showAutoFollowExperimentContent =
		fg('cc-engagement-auto-follow-recommended-spaces-gate') && Boolean(hasAutoFollowedSpaces);

	const TWO_WEEKS_IN_DAYS = 14;
	const dateToday = new Date();
	const bannerShownTimestamp = autoFollowBannerShownAt ? new Date(autoFollowBannerShownAt) : null;
	const isValidBannerTimestamp = bannerShownTimestamp && !isNaN(bannerShownTimestamp.getTime());

	useEffect(() => {
		// banner timestamps
		if (
			followedUsers.length > 0 &&
			hasAutoFollowedTeammates &&
			!autoFollowBannerShownAt &&
			!onboardingStateLoading &&
			fg('autofollow-teammates-preconditions')
		) {
			setOnboardingState({
				key: NTH_USER_AUTO_FOLLOW_TEAMMATES.AUTO_FOLLOW_BANNER_SHOWN_AT,
				value: new Date().toISOString(),
			});
		}
	}, [
		followedUsers.length,
		hasAutoFollowedTeammates,
		autoFollowBannerShownAt,
		onboardingStateLoading,
		setOnboardingState,
	]);

	const showAutofollowTeammatesContent =
		followedUsers.length > 0 &&
		hasAutoFollowedTeammates &&
		!autoFollowBannerDismissed &&
		(!isValidBannerTimestamp ||
			differenceInDays(dateToday, bannerShownTimestamp) <= TWO_WEEKS_IN_DAYS) &&
		fg('autofollow-teammates-preconditions');

	const teammateCount = followedUsers.length;
	const name1 = followedUsers[0]?.displayName || '';
	const name2 = followedUsers[1]?.displayName || '';

	const contentConfig = useMemo(() => {
		if (showAutofollowTeammatesContent) {
			// < 10 users
			return getAutofollowTeammatesContentConfig(teammateCount, name1, name2);
		}
		if (showAutoFollowExperimentContent) {
			// > 10 users
			return autoFollowContentConfig;
		}
		return defaultContentConfig;
	}, [
		showAutoFollowExperimentContent,
		showAutofollowTeammatesContent,
		teammateCount,
		name1,
		name2,
	]);

	const error = featureDiscoveryError || onboardingStateError;

	useEffect(() => {
		if (showFeature && servingRecommendations && !error && !onboardingStateLoading) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'feedOnboardingMessage',
					attributes: {
						isAutoFollowContent: showAutoFollowExperimentContent,
					},
				},
			}).fire();
		}
	}, [
		createAnalyticsEvent,
		error,
		showFeature,
		servingRecommendations,
		showAutoFollowExperimentContent,
		onboardingStateLoading,
	]);

	if (error) {
		return <ErrorDisplay error={error} />;
	}

	if (
		!showAutofollowTeammatesContent &&
		(!showFeature || !servingRecommendations || onboardingStateLoading)
	) {
		return null;
	}

	const handleClick = () => {
		openActivityFilterModal();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'feedOnboardingUpdateFeed',
				source: 'feedOnboardingMessage',
				attributes: {
					isAutoFollowContent: showAutoFollowExperimentContent,
				},
			},
		}).fire();
	};

	const handleDismiss = () => {
		if (showAutofollowTeammatesContent) {
			setOnboardingState({
				key: NTH_USER_AUTO_FOLLOW_TEAMMATES.AUTO_FOLLOW_BANNER_DISMISSED,
				value: 'true',
			});
		}
		stop();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'feedOnboardingDismiss',
				source: 'feedOnboardingMessage',
				attributes: {
					isAutoFollowContent: showAutoFollowExperimentContent,
					...(showAutofollowTeammatesContent && { isAutofollowTeammatesContent: true }),
				},
			},
		}).fire();
	};

	if (showAutofollowTeammatesContent) {
		return (
			<Box
				xcss={autofollowTeammatesStyles.autofollowTeammatesContainer}
				testId="feed-onboarding-message"
				data-vc="feed-onboarding-message-teammates"
			>
				<Box xcss={autofollowTeammatesStyles.autofollowAvatarContainer}>
					{followedUsers.slice(0, 2).map((user, index) => (
						<Box
							key={user.accountId || index}
							xcss={index > 0 ? autofollowTeammatesStyles.avatarOverlap : undefined}
						>
							<Avatar size="small" src={user.profilePicture?.path} name={user.displayName} />
						</Box>
					))}
				</Box>

				<Box xcss={autofollowTeammatesStyles.autofollowTeammatesMessage}>
					<Stack space="space.050">
						{contentConfig.title}
						<Box xcss={autofollowTeammatesStyles.autofollowTeammatesDescription}>
							{contentConfig.description}
						</Box>
					</Stack>
				</Box>

				<Box xcss={autofollowTeammatesStyles.autofollowCloseButtonContainer}>
					<IconButton
						onClick={handleDismiss}
						spacing="default"
						appearance="subtle"
						icon={CrossIcon}
						label={intl.formatMessage(i18n.autofollowBannerDismiss)}
						isTooltipDisabled={false}
					/>
				</Box>
			</Box>
		);
	}

	return (
		<BodyContainer data-testid="feed-onboarding-message" data-vc="feed-onboarding-message">
			<MessageContainer>
				<Stack space="space.100">
					{contentConfig.title}
					<Box xcss={descriptionTextStyles}>{contentConfig.description}</Box>
				</Stack>
				<Fragment>
					<Button onClick={handleClick}>{contentConfig.button}</Button>
				</Fragment>
			</MessageContainer>

			<OnBoardingImage
				src={contentConfig.lightImage}
				srcDark={contentConfig.darkImage}
				width="200"
				height="160"
				data-vc="following-onboarding-image"
			/>

			<CloseButtonContainer>
				<IconButton
					onClick={handleDismiss}
					spacing="compact"
					appearance="subtle"
					icon={CrossIcon}
					label={intl.formatMessage(i18n.closeMessageLabel)}
					isTooltipDisabled={false}
				/>
			</CloseButtonContainer>
		</BodyContainer>
	);
};

export const FeedOnboardingMessage = injectIntl(FeedOnboardingMessageComponent);
